import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from "react";

const waitForSModalDefinition = async () => {
  if (typeof customElements !== "undefined" && !customElements.get("s-modal")) {
    await Promise.race([
      customElements.whenDefined("s-modal"),
      new Promise((resolve) => {
        setTimeout(resolve, 1000);
      }),
    ]);
  }
};

const isModalElementReady = (modal) =>
  Boolean(
    modal &&
      (typeof modal.showOverlay === "function" ||
        typeof modal.hideOverlay === "function")
  );

// Prefer the instance ref so duplicate `id` values cannot hijack another modal.
const getModalElement = async (modalRef, modalId) => {
  if (isModalElementReady(modalRef.current)) {
    return modalRef.current;
  }

  await waitForSModalDefinition();

  if (isModalElementReady(modalRef.current)) {
    return modalRef.current;
  }

  const existingModal = document.getElementById(modalId);
  if (isModalElementReady(existingModal)) {
    return existingModal;
  }

  return modalRef.current || existingModal;
};

const BLOCK_HEADING_TAGS = new Set([
  "p",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "li",
]);

const extractTextNodes = (node) => {
  if (node === null || node === undefined || typeof node === "boolean") {
    return [];
  }
  if (typeof node === "string" || typeof node === "number") {
    return [String(node)];
  }
  if (Array.isArray(node)) {
    return node.flatMap(extractTextNodes);
  }
  if (React.isValidElement(node)) {
    const childParts = extractTextNodes(node.props?.children);
    const text = childParts.join("").replace(/\s+/g, " ").trim();
    if (!text) {
      return [];
    }

    const tag =
      typeof node.type === "string" ? node.type.toLowerCase() : "";
    const separator = BLOCK_HEADING_TAGS.has(tag) ? " · " : " ";
    return [text, separator];
  }
  return [];
};

const isReactHeading = (heading) =>
  React.isValidElement(heading) || Array.isArray(heading);

// `heading` lands on s-modal as an attribute, so a React element passed here
// would be stringified to "[object Object]". Fall back to its text content.
const normalizeHeading = (heading) => {
  if (heading === null || heading === undefined) {
    return undefined;
  }
  if (typeof heading === "string") {
    return heading;
  }
  if (typeof heading === "number") {
    return String(heading);
  }

  const text = extractTextNodes(heading)
    .join("")
    .replace(/\s*·\s*$/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (process.env.NODE_ENV !== "production" && isReactHeading(heading)) {
    // eslint-disable-next-line no-console
    console.warn(
      "[ShopifyModal] `heading` must be a string for s-modal. React nodes are converted to plain text; pass a string directly when possible.",
      heading
    );
  }

  return text || undefined;
};

const normalizeActions = (actions) => {
  if (!actions) {
    return [];
  }
  return Array.isArray(actions) ? actions : [actions];
};

const getModalDialogElement = (modal) => {
  if (!modal?.shadowRoot) {
    return null;
  }

  return modal.shadowRoot.querySelector("dialog");
};

const isModalOverlayVisible = (modal) => {
  const dialog = getModalDialogElement(modal);
  return Boolean(dialog?.open);
};

const hasOtherVisibleModals = (exceptModalId) =>
  Array.from(document.querySelectorAll("s-modal")).some(
    (el) => el.id !== exceptModalId && isModalOverlayVisible(el)
  );

const waitUntilNoOtherModalsVisible = (exceptModalId, maxWaitMs = 600) =>
  new Promise((resolve) => {
    if (!hasOtherVisibleModals(exceptModalId)) {
      resolve();
      return;
    }

    let resolved = false;
    const deadline = Date.now() + maxWaitMs;
    const otherModals = Array.from(document.querySelectorAll("s-modal")).filter(
      (el) => el.id !== exceptModalId
    );

    const finish = () => {
      if (resolved) {
        return;
      }
      resolved = true;
      otherModals.forEach((modal) => {
        modal.removeEventListener("hide", onOtherHide);
      });
      resolve();
    };

    const onOtherHide = (event) => {
      if (event.target?.localName !== "s-modal") {
        return;
      }
      if (!hasOtherVisibleModals(exceptModalId)) {
        requestAnimationFrame(() => requestAnimationFrame(finish));
      }
    };

    otherModals.forEach((modal) => {
      modal.addEventListener("hide", onOtherHide);
    });

    const poll = () => {
      if (!hasOtherVisibleModals(exceptModalId)) {
        requestAnimationFrame(() => requestAnimationFrame(finish));
        return;
      }
      if (Date.now() >= deadline) {
        finish();
        return;
      }
      requestAnimationFrame(poll);
    };

    requestAnimationFrame(poll);
    setTimeout(finish, maxWaitMs);
  });

/**
 * Polaris Modal compatible wrapper around Shopify App Home s-modal.
 *
 * Dropdowns inside the modal must use s-popover/s-tooltip with commandFor.
 * Polaris Popover/Tooltip/Autocomplete portal into document.body, which sits
 * below the modal's top layer, and a plain absolutely positioned list gets
 * clipped by the shadow-DOM scroll box wrapping the content slot.
 *
 * s-popover positions itself from its activator's bounding rect, so the
 * activator must stay mounted for as long as the popover can be open.
 */
const ShopifyModal = forwardRef(
  (
    {
      id,
      open = false,
      onClose,
      heading,
      title,
      size = "base",
      padding = "base",
      accessibilityLabel,
      primaryAction,
      secondaryActions,
      showCloseButton = true,
      closeText = "Close",
      children,
    },
    ref
  ) => {
    const generatedId = useId().replace(/:/g, "");
    const modalId = id || `shopify-modal-${generatedId}`;
    const modalRef = useRef(null);
    const suppressCloseSideEffects = useRef(false);
    const modalWasVisibleRef = useRef(false);
    const closedByUserRef = useRef(false);
    const isHandlingCloseRef = useRef(false);
    const onCloseRef = useRef(onClose);
    const openRef = useRef(open);

    onCloseRef.current = onClose;
    openRef.current = open;

    useImperativeHandle(ref, () => ({
      hideOverlay: () => {
        if (typeof modalRef.current?.hideOverlay === "function") {
          modalRef.current.hideOverlay();
        }
      },
      showOverlay: () => {
        if (typeof modalRef.current?.showOverlay === "function") {
          modalRef.current.showOverlay();
        }
      },
      getElement: () => modalRef.current,
    }));

    useEffect(() => {
      let isActive = true;
      let modalElement = null;

      // Once `hide` is dispatched the dialog is already closing, so reverting
      // with showOverlay() here would desync the React `open` prop from the real
      // dialog state and leave an open overlay with unmounted children. Unwanted
      // dismissals must be blocked before they reach s-modal, not undone after.
      const handleHide = (event) => {
        // s-popover/s-tooltip dispatch `hide` with bubbles:true, so a nested
        // overlay closing reaches this listener and would close the modal.
        // s-modal's own `hide` does not bubble and targets the modal itself.
        if (event.target !== modalElement) {
          return;
        }
        if (suppressCloseSideEffects.current) {
          suppressCloseSideEffects.current = false;
          return;
        }
        if (isHandlingCloseRef.current) {
          return;
        }
        isHandlingCloseRef.current = true;
        closedByUserRef.current = true;
        onCloseRef.current?.();
        isHandlingCloseRef.current = false;
      };

      getModalElement(modalRef, modalId).then((el) => {
        if (!el || !isActive) {
          return;
        }
        modalElement = el;
        modalElement.addEventListener("hide", handleHide);
      });

      return () => {
        isActive = false;
        modalElement?.removeEventListener("hide", handleHide);
      };
    }, [modalId]);

    // s-modal dismisses itself from a bubble-phase click listener on its shadow
    // <dialog>, guarded by `event.target === dialog` — which is true both for
    // the backdrop and for the panel's own padding. Intercepting in the capture
    // phase on the host runs before anything inside the shadow tree, so
    // hideOverlay() is never reached and no `hide` event is dispatched. Escape
    // is a separate keydown listener and stays functional.
    useEffect(() => {
      if (!open) {
        return undefined;
      }

      let isActive = true;
      let modalElement = null;

      const blockBackdropDismiss = (event) => {
        const dialog = getModalDialogElement(modalElement);
        if (dialog && event.composedPath()[0] === dialog) {
          event.stopImmediatePropagation();
        }
      };

      getModalElement(modalRef, modalId).then((el) => {
        if (!el || !isActive) {
          return;
        }
        modalElement = el;
        modalElement.addEventListener("click", blockBackdropDismiss, true);
      });

      return () => {
        isActive = false;
        modalElement?.removeEventListener("click", blockBackdropDismiss, true);
      };
    }, [open, modalId]);

    useEffect(() => {
      let isActive = true;

      if (!open && modalWasVisibleRef.current) {
        suppressCloseSideEffects.current = !closedByUserRef.current;
      }

      getModalElement(modalRef, modalId).then((modal) => {
        if (!modal || !isActive) {
          return;
        }

        if (open) {
          modalWasVisibleRef.current = true;
          waitUntilNoOtherModalsVisible(modalId).then(() => {
            if (!isActive || !openRef.current) {
              return;
            }
            if (typeof modal.showOverlay === "function") {
              modal.showOverlay();
            }
          });
        } else if (modalWasVisibleRef.current) {
          if (closedByUserRef.current) {
            closedByUserRef.current = false;
            return;
          }
          if (typeof modal.hideOverlay === "function") {
            modal.hideOverlay();
          }
        }
      });

      return () => {
        isActive = false;
      };
    }, [open, modalId]);

    const secondaryActionList = normalizeActions(secondaryActions);
    const shouldRenderDefaultClose =
      showCloseButton && secondaryActionList.length === 0;

    const rawHeading = heading ?? title;
    const modalHeading = normalizeHeading(rawHeading);
    const resolvedAccessibilityLabel =
      accessibilityLabel ||
      (isReactHeading(rawHeading) ? modalHeading : undefined);

    return (
      <s-modal
        id={modalId}
        ref={modalRef}
        heading={modalHeading}
        size={size}
        padding={padding}
        accessibilityLabel={resolvedAccessibilityLabel}
      >
        {open ? (
          <div className="shopify-modal-content-shell">{children}</div>
        ) : null}

        {secondaryActionList.map((action, index) => {
          const { content, onAction, disabled, loading, tone = "auto" } = action;

          if (onAction) {
            return (
              <s-button
                key={`secondary-${index}-${content}`}
                slot="secondary-actions"
                variant="secondary"
                tone={tone}
                disabled={disabled || undefined}
                loading={loading || undefined}
                onClick={onAction}
              >
                {content}
              </s-button>
            );
          }

          return (
            <s-button
              key={`secondary-${index}-${content}`}
              slot="secondary-actions"
              variant="secondary"
              tone={tone}
              disabled={disabled || undefined}
              loading={loading || undefined}
              commandFor={modalId}
              command="--hide"
            >
              {content}
            </s-button>
          );
        })}

        {shouldRenderDefaultClose && (
          <s-button
            slot="secondary-actions"
            commandFor={modalId}
            command="--hide"
          >
            {closeText}
          </s-button>
        )}

        {primaryAction && (
          <s-button
            slot="primary-action"
            variant="primary"
            tone={primaryAction.destructive ? "critical" : primaryAction.tone || "auto"}
            disabled={primaryAction.disabled || undefined}
            loading={primaryAction.loading || undefined}
            onClick={primaryAction.onAction}
          >
            {primaryAction.content}
          </s-button>
        )}
      </s-modal>
    );
  }
);

ShopifyModal.displayName = "ShopifyModal";

export default ShopifyModal;
